import React, { useState, useRef, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAutosave } from "@/lib/useAutosave";
import { useDraftBackup, DraftRecoverBanner } from "@/components/app/DraftRecover";
import { FileText, X, Save, Loader2, Plus, Trash2, Palette } from "lucide-react";
import RichText from "@/components/app/RichText";
import FirmasEditor from "@/components/app/FirmasEditor";

export const PLAN_THEMES = [
  { n: 1, color: "#0b2a5b", name: "Marino" },
  { n: 2, color: "#7c3aed", name: "Violeta" },
  { n: 3, color: "#dc2626", name: "Rojo" },
  { n: 4, color: "#16a34a", name: "Verde" },
  { n: 5, color: "#f97316", name: "Naranja" },
  { n: 6, color: "#0891b2", name: "Cian" },
  { n: 7, color: "#db2777", name: "Rosa" },
  { n: 8, color: "#ca8a04", name: "Ámbar" },
  { n: 9, color: "#4f46e5", name: "Índigo" },
  { n: 10, color: "#0f172a", name: "Grafito" },
];
const PALETTE = ["#0b2a5b", "#7c3aed", "#dc2626", "#16a34a", "#f97316", "#0891b2", "#db2777", "#ca8a04", "#4f46e5", "#0f172a", "#2563eb", "#059669", "#e11d48", "#000000"];

const EJES_SEC = ["Inclusión", "Pensamiento crítico", "Interculturalidad crítica", "Igualdad de género", "Vida saludable", "Artes", "Lectura y escritura", "Apropiación de las culturas"];
const METODOLOGIAS_SEC = ["Aprendizaje Basado en Proyectos", "Aprendizaje Basado en Problemas", "STEAM", "Servicio Comunitario", "Investigación", "Otra"];
const INSTR_SEC = ["Lista de cotejo", "Rúbrica", "Escala estimativa", "Diario", "Portafolio", "Examen"];
const EVIDENCIAS = ["Fotografías", "Trabajos", "Videos", "Portafolio"];

const DATOS_SEC = [["escuela", "Escuela"], ["cct", "CCT"], ["ciclo", "Ciclo escolar"], ["docente", "Docente"], ["asignatura", "Asignatura / Campo"], ["fase", "Fase"], ["grado", "Grado"], ["grupo", "Grupo"], ["periodo", "Periodo"], ["num_sesiones", "N.º de sesiones"]];

// -------- Formato oficial de PREESCOLAR (Nueva Escuela Mexicana) --------
const NAVY_BAND = "#12315f";
const GOLD = "#e6a51e";
const GREEN = "#5f9f46";
const CELL = "#c9cccf";
const EJES_PRE_NEW = ["Inclusión", "Pensamiento crítico", "Interculturalidad crítica", "Igualdad de género", "Apropiación de las culturas a través de la lectura y la escritura", "Vida saludable", "Artes y experiencias estéticas"];
const MODALIDADES_PRE = ["Taller crítico", "Rincón de aprendizaje", "Centros de interés", "Unidad didáctica", "Aprendizaje basado en el juego", "Proyecto"];
const CAMPOS_PRE = [
  { key: "len", label: "Lenguajes", bg: "#8f2436", fg: "#ffffff" },
  { key: "sab", label: "Saberes y pensamiento científico", bg: "#2f5aa0", fg: "#ffffff" },
  { key: "eti", label: "Ética, naturaleza y sociedades", bg: "#4c9a3f", fg: "#ffffff" },
  { key: "hum", label: "De lo humano y lo comunitario", bg: "#eef2f8", fg: "#12315f" },
];
const LIBROS_PRE = [
  { key: "libro_explorar", label: "Explorar e imaginar" },
  { key: "libro_juega", label: "Juega e imagina" },
  { key: "libro_multiples", label: "Múltiples lenguajes" },
  { key: "libro_laminas", label: "Láminas de diálogo" },
];
const MEJORA_PRE = [
  { key: "integridad", label: "Medidas preventivas para salvaguardar la integridad de los estudiantes" },
  { key: "fechas", label: "Fechas conmemorativas" },
  { key: "financiera", label: "Educación financiera" },
  { key: "vida", label: "Vida saludable" },
  { key: "lectura", label: "Círculo de lectura" },
  { key: "cuenta", label: "Proyecto cuenta cuentos" },
  { key: "ajedrez", label: "Neurodesarrollo ajedrez" },
];

// -------- Formato oficial de PRIMARIA/SECUNDARIA (Plan de trabajo NEM) --------
const STEEL = "#9db3c7";
const ICON = (name) => `/nem-icons/${name}.jpeg`;
const NIVEL_ESTILOS = [["kines", "Kinestésico"], ["visual", "Visual"], ["auditivo", "Auditivo"], ["kines_visual", "Kinestésico-visual"]];
const EJES_PRIM = [
  { key: "Interculturalidad crítica", icon: "eje_interculturalidad" },
  { key: "Pensamiento crítico", icon: "eje_pensamiento_critico" },
  { key: "Vida saludable", icon: "eje_vida_saludable" },
  { key: "Artes y experiencias estéticas", icon: "eje_artes" },
  { key: "Apropiación de las culturas a través de la lectura y la escritura", icon: "eje_lectura" },
  { key: "Inclusión", icon: "eje_inclusion" },
  { key: "Igualdad de género", icon: "eje_igualdad_genero" },
];
const CAMPOS_PRIM = [
  { key: "len", label: "Lenguajes", icon: "campo_lenguajes" },
  { key: "sab", label: "Saberes y pensamiento científico", icon: "campo_saberes" },
  { key: "eti", label: "Ética, naturaleza y sociedades", icon: "campo_etica" },
  { key: "hum", label: "De lo humano y lo comunitario", icon: "campo_humano" },
];
const METODOLOGIAS_PRIM = [["met_abpc", "ABPC Comunidad"], ["met_abp", "ABP Problemas"], ["met_abi", "ABI Indagación"], ["met_as", "AS · Servicio"]];
const DIAS_PRIM = [["hor_lunes", "Lunes"], ["hor_martes", "Martes"], ["hor_miercoles", "Miércoles"], ["hor_jueves", "Jueves"], ["hor_viernes", "Viernes"]];
const APOYO_PRIM = [["rezago", "Listado de alumnos con rezago"], ["bap", "Listado de alumnos BAP"], ["apoyo", "Listado de alumnos que requieren apoyo"]];
const SABERES_PRIM = [["saber_que", "Saber qué · Declarativo"], ["saber_hacer", "Saber hacer · Procedimental"], ["saber_ser", "Saber ser · Actitudinal"]];

// IMPORTANTE: componentes a nivel de módulo (identidad estable) para que el <textarea>
// no pierda el cursor al escribir (React remontaría si se definen dentro del componente).
const H = ({ color, children }) => (
  <div className="font-display font-bold text-sm mt-4 mb-2 pb-1 border-b-2" style={{ color, borderColor: color }}>{children}</div>
);

const Area = ({ value, onChange, label, rows = 3, ph, testid, color, files, onFilesChange }) => (
  <div className="mb-2">
    <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500 block mb-1">{label}</label>
    <RichText value={value} onChange={onChange} placeholder={ph} testid={testid} rows={rows} color={color}
      files={files} onFilesChange={onFilesChange} label={label} variant="bordered" />
  </div>
);

const Checks = ({ selected, onToggle, opts, label, testidBase, color }) => (
  <div className="mb-2">
    <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500 block mb-1">{label}</label>
    <div className="flex flex-wrap gap-1.5">
      {opts.map((o) => {
        const on = (selected || []).includes(o);
        return (
          <button type="button" key={o} onClick={() => onToggle(o)} data-testid={`${testidBase}-${o}`}
            className="text-[11px] px-2.5 py-1 rounded-full border transition-colors"
            style={on ? { background: color, borderColor: color, color: "#fff" } : { background: "#fff", borderColor: "#e2e8f0", color: "#475569" }}>{o}</button>
        );
      })}
    </div>
  </div>
);

// Recuadro de escritura enriquecido (tipo Word) usado en tablas del formato preescolar.
const AutoTextarea = ({ value, onChange, placeholder, testid, minRows = 2 }) => (
  <RichText value={value} onChange={onChange} placeholder={placeholder} testid={testid} minRows={minRows} variant="plain" />
);

const Band = ({ children, bg = NAVY_BAND, color = "#fff" }) => (
  <div className="text-center font-bold text-[11px] uppercase tracking-wide px-2 py-1.5" style={{ background: bg, color }}>{children}</div>
);

export default function PlaneacionFormato({ tipo, initial, defaultDocente, onSaved, onCancel }) {
  const isPre = tipo === "preescolar";
  const [titulo, setTitulo] = useState(initial?.proyecto || "");
  const [color, setColor] = useState(initial?.color || PLAN_THEMES[0].color);
  const [formato, setFormato] = useState(initial?.formato || 1);
  const [d, setD] = useState({
    docente: defaultDocente || "", ejes: [], instrumento: [], metodologia: [], evidencias: [],
    sesiones: [{ inicio: "", desarrollo: "", cierre: "" }],
    estructura: [{ fecha: "", estructura: "", recursos: "" }],
    mejora: {},
    _files: {},
    firma_docente_name: "", firma_docente_rol: "Docente",
    firma_director_name: "", firma_director_rol: "Director(a)",
    firma_coord_name: "", firma_coord_rol: "Coordinador(a)",
    ...(initial?.data || {}),
  });
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(initial?.id || null);

  const set = (k) => (e) => setD((s) => ({ ...s, [k]: e.target.value }));
  const setFiles = (k) => (arr) => setD((s) => ({ ...s, _files: { ...(s._files || {}), [k]: arr } }));
  const filesOf = (k) => (d._files || {})[k] || [];
  const toggle = (k, v) => setD((s) => ({ ...s, [k]: (s[k] || []).includes(v) ? s[k].filter((x) => x !== v) : [...(s[k] || []), v] }));
  const setSes = (i, k, v) => setD((s) => ({ ...s, sesiones: s.sesiones.map((x, j) => j === i ? { ...x, [k]: v } : x) }));
  const addSes = () => setD((s) => ({ ...s, sesiones: [...(s.sesiones || []), { inicio: "", desarrollo: "", cierre: "" }] }));
  const delSes = (i) => setD((s) => ({ ...s, sesiones: s.sesiones.filter((_, j) => j !== i) }));

  // Preescolar helpers
  const setEstr = (i, k, v) => setD((s) => ({ ...s, estructura: (s.estructura || []).map((x, j) => j === i ? { ...x, [k]: v } : x) }));
  const addEstr = () => setD((s) => ({ ...s, estructura: [...(s.estructura || []), { fecha: "", estructura: "", recursos: "" }] }));
  const delEstr = (i) => setD((s) => ({ ...s, estructura: (s.estructura || []).filter((_, j) => j !== i) }));
  const setMejora = (pk, field, v) => setD((s) => ({ ...s, mejora: { ...(s.mejora || {}), [pk]: { ...((s.mejora || {})[pk] || {}), [field]: v } } }));

  const pickFormato = (t) => { setFormato(t.n); setColor(t.color); };

  const area = (k, label, rows, ph) => (
    <Area value={d[k] || ""} onChange={set(k)} label={label} rows={rows} ph={ph} testid={`pf-${k}`} color={color}
      files={filesOf(k)} onFilesChange={setFiles(k)} />
  );
  const checks = (k, opts, label) => (
    <Checks selected={d[k]} onToggle={(v) => toggle(k, v)} opts={opts} label={label} testidBase={`pf-${k}`} color={color} />
  );
  // Textareas grandes/expansibles del formato preescolar (con adjuntar archivos)
  const ta = (k, ph = "", minRows = 2) => (
    <RichText value={d[k] || ""} onChange={set(k)} placeholder={ph} testid={`pf-${k}`} minRows={minRows} variant="plain" color={color}
      files={filesOf(k)} onFilesChange={setFiles(k)} label={k} />
  );
  const tam = (pk, field) => (
    <AutoTextarea value={(d.mejora?.[pk]?.[field]) || ""} onChange={(e) => setMejora(pk, field, e.target.value)} testid={`pf-mejora-${pk}-${field}`} minRows={2} />
  );

  const savedSnap = useRef("");
  const savingRef = useRef(false);
  const draft = useDraftBackup({ tipo: `fmt-${tipo}`, initialId: initial?.id });
  const applyDraft = (p) => { if (!p) return; setTitulo(p.titulo || ""); if (p.color) setColor(p.color); if (p.formato) setFormato(p.formato); if (p.d) setD(p.d); };
  const save = async (silent = false) => {
    const body = { proyecto: titulo.trim() || "Planeación sin título", tipo, color, formato, data: d };
    const snap = JSON.stringify(body);
    if (silent && snap === savedSnap.current) return;   // nada nuevo que guardar
    if (savingRef.current) return;                        // evita guardados simultáneos (no duplica)
    savingRef.current = true;
    if (!silent) setSaving(true);
    try {
      if (savedId) await api.put(`/docente/planeaciones/${savedId}`, body);
      else { const { data: res } = await api.post("/docente/planeaciones", body); setSavedId(res.id); }
      savedSnap.current = snap;
      draft.clear();
      if (!silent) onSaved();
    } catch (e) { if (!silent) toast.error(e?.response?.data?.detail || "No se pudo guardar"); }
    finally { savingRef.current = false; if (!silent) setSaving(false); }
  };
  const baseWatch = useRef();
  const watch = JSON.stringify(d) + "|" + titulo + "|" + color + "|" + formato;
  if (baseWatch.current === undefined) baseWatch.current = watch;
  useAutosave(watch, () => { draft.backup({ titulo, color, formato, d }); save(true); }, { enabled: watch !== baseWatch.current });

  return (
    <div className="fixed inset-0 z-[80] bg-slate-900/80 flex items-start justify-center overflow-y-auto p-4" onClick={onCancel} data-testid="pf-modal">
      <div className={`bg-white rounded-2xl shadow-2xl w-full my-8 ${isPre ? "max-w-5xl" : "max-w-3xl"}`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 rounded-t-2xl text-white" style={{ background: color }}>
          <div className="font-display font-bold flex items-center gap-2"><FileText className="w-4 h-4" /> {initial?.id ? "Editar" : "Nueva"} planeación · {isPre ? "Preescolar" : "Primaria y Secundaria"}</div>
          <button onClick={onCancel} className="text-white/80 hover:text-white p-1"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-4 sm:px-5 pt-3">
          <DraftRecoverBanner pending={draft.pending} onRecover={() => { applyDraft(draft.pending); draft.consumed(); }} onDismiss={draft.dismiss} />
        </div>

        {isPre ? (
          /* ---------- FORMATO OFICIAL DE PREESCOLAR (tipo tabla) ---------- */
          <div className="p-4 sm:p-5">
            <input value={titulo} onChange={(e) => setTitulo(e.target.value)} placeholder="Nombre de la planeación (para tu lista)" data-testid="pf-titulo"
              className="w-full border border-nardo-200 rounded-lg px-3 py-2.5 text-sm text-navy outline-none focus:border-navy font-semibold mb-3" />

            <div className="mb-1 flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-nardo-500"><Palette className="w-3 h-3" /> Formato (color) — 10 opciones</div>
            <div className="flex flex-wrap gap-1.5 mb-2" data-testid="pf-formatos">
              {PLAN_THEMES.map((t) => (
                <button key={t.n} type="button" onClick={() => pickFormato(t)} title={`Formato ${t.n} · ${t.name}`} data-testid={`pf-formato-${t.n}`}
                  className={`w-8 h-8 rounded-lg border-2 flex items-center justify-center text-[10px] font-bold text-white ${formato === t.n ? "ring-2 ring-offset-1" : ""}`}
                  style={{ background: t.color, borderColor: formato === t.n ? t.color : "transparent" }}>{t.n}</button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-1.5 mb-4">
              <span className="text-[10px] text-nardo-400">Color personalizado:</span>
              {PALETTE.map((c) => <button key={c} type="button" onClick={() => setColor(c)} data-testid={`pf-color-${c}`} style={{ background: c }} className={`w-5 h-5 rounded border ${color === c ? "ring-2 ring-offset-1 ring-navy" : "border-nardo-200"}`} />)}
              <input type="color" value={color} onChange={(e) => setColor(e.target.value)} data-testid="pf-color-custom" className="w-7 h-6 rounded border border-nardo-200 cursor-pointer" />
            </div>

            <div className="border rounded-lg overflow-hidden text-navy" style={{ borderColor: CELL }} data-testid="pf-pre">
              {/* Encabezado */}
              <div className="grid grid-cols-2 sm:grid-cols-6" style={{ borderBottom: `1px solid ${CELL}` }}>
                {[["docente", "Docente"], ["grupo", "Grupo"], ["grado", "Grado"], ["no_ninas", "No. Niñas"], ["no_ninos", "No. Niños"], ["total", "Total"]].map(([k, l], i) => (
                  <div key={k} className="p-1.5" style={{ borderRight: i < 5 ? `1px solid ${CELL}` : "none" }}>
                    <label className="block text-[9px] uppercase tracking-wide text-nardo-500 font-semibold">{l}</label>
                    <input value={d[k] || ""} onChange={set(k)} data-testid={`pf-${k}`} placeholder="Escribe aquí" className="w-full text-sm outline-none bg-transparent text-navy border-b border-dashed border-nardo-300 focus:border-navy placeholder:text-nardo-300 placeholder:text-[11px]" />
                  </div>
                ))}
              </div>

              {/* Proyecto / Temporalidad · Problemática / Perfil de egreso */}
              <div className="grid sm:grid-cols-2" style={{ borderBottom: `1px solid ${CELL}` }}>
                <div style={{ borderRight: `1px solid ${CELL}` }}>
                  <div className="flex items-center" style={{ borderBottom: `1px solid ${CELL}` }}>
                    <div className="w-28 shrink-0 px-2 py-1.5 font-bold text-[11px] bg-slate-100" style={{ borderRight: `1px solid ${CELL}` }}>PROYECTO</div>
                    <input value={titulo} onChange={(e) => setTitulo(e.target.value)} data-testid="pf-proyecto-cell" placeholder="Escribe aquí" className="flex-1 px-2 py-1.5 text-sm outline-none bg-transparent placeholder:text-nardo-300 placeholder:text-[11px]" />
                  </div>
                  <div className="flex">
                    <div className="w-28 shrink-0 px-2 py-1.5 font-bold text-[11px] bg-slate-100" style={{ borderRight: `1px solid ${CELL}` }}>PROBLEMÁTICA</div>
                    <div className="flex-1">{ta("problematica", "")}</div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center" style={{ borderBottom: `1px solid ${CELL}` }}>
                    <div className="w-32 shrink-0 px-2 py-1.5 font-bold text-[11px] bg-slate-100" style={{ borderRight: `1px solid ${CELL}` }}>TEMPORALIDAD</div>
                    <input value={d.temporalidad || ""} onChange={set("temporalidad")} data-testid="pf-temporalidad" placeholder="Escribe aquí" className="flex-1 px-2 py-1.5 text-sm outline-none bg-transparent placeholder:text-nardo-300 placeholder:text-[11px]" />
                  </div>
                  <div className="flex">
                    <div className="w-32 shrink-0 px-2 py-1.5 font-bold text-[11px] bg-slate-100" style={{ borderRight: `1px solid ${CELL}` }}>PERFIL DE EGRESO</div>
                    <div className="flex-1">{ta("perfil_egreso", "")}</div>
                  </div>
                </div>
              </div>

              {/* Ejes articuladores · Modalidad */}
              <div className="grid sm:grid-cols-2" style={{ borderBottom: `1px solid ${CELL}` }}>
                <div style={{ borderRight: `1px solid ${CELL}` }}>
                  <Band>Ejes articuladores</Band>
                  <div className="p-2 flex flex-wrap gap-1.5">
                    {EJES_PRE_NEW.map((o) => {
                      const on = (d.ejes || []).includes(o);
                      return <button type="button" key={o} onClick={() => toggle("ejes", o)} data-testid={`pf-eje-${o}`}
                        className="text-[11px] px-2.5 py-1 rounded-full border transition-colors"
                        style={on ? { background: NAVY_BAND, borderColor: NAVY_BAND, color: "#fff" } : { background: "#fff", borderColor: "#e2e8f0", color: "#475569" }}>{o}</button>;
                    })}
                  </div>
                </div>
                <div>
                  <Band>Modalidad</Band>
                  <div className="p-2 flex flex-wrap gap-1.5">
                    {MODALIDADES_PRE.map((o) => {
                      const on = d.modalidad === o;
                      return <button type="button" key={o} onClick={() => setD((s) => ({ ...s, modalidad: o }))} data-testid={`pf-modalidad-${o}`}
                        className="text-[11px] px-2.5 py-1 rounded-full border transition-colors"
                        style={on ? { background: GOLD, borderColor: GOLD, color: "#3a2a00" } : { background: "#fff", borderColor: "#e2e8f0", color: "#475569" }}>{o}</button>;
                    })}
                  </div>
                </div>
              </div>

              {/* Campo formativo · Contenido · PDA */}
              <div style={{ borderBottom: `1px solid ${CELL}` }}>
                <div className="grid" style={{ gridTemplateColumns: "150px 1fr 1fr" }}>
                  <div style={{ borderRight: `1px solid ${CELL}` }}><Band bg={GOLD} color="#3a2a00">Articulación de campo formativo</Band></div>
                  <div style={{ borderRight: `1px solid ${CELL}` }}><Band bg={GOLD} color="#3a2a00">Contenido</Band></div>
                  <div><Band bg={GOLD} color="#3a2a00">PDA</Band></div>
                </div>
                {CAMPOS_PRE.map((c) => (
                  <div key={c.key} className="grid" style={{ gridTemplateColumns: "150px 1fr 1fr", borderTop: `1px solid ${CELL}` }}>
                    <div className="flex items-center px-2 py-1 text-[11px] font-bold" style={{ background: c.bg, color: c.fg }}>{c.label}</div>
                    <div style={{ borderLeft: `1px solid ${CELL}` }}>{ta(`cont_${c.key}`, "", 2)}</div>
                    <div style={{ borderLeft: `1px solid ${CELL}` }}>{ta(`pda_${c.key}`, "", 2)}</div>
                  </div>
                ))}
              </div>

              {/* Estructura de la modalidad */}
              <div style={{ borderBottom: `1px solid ${CELL}` }}>
                <Band bg={GOLD} color="#3a2a00">Estructura de la modalidad</Band>
                <div className="grid text-[10px] font-bold bg-slate-50" style={{ gridTemplateColumns: "110px 1fr 150px 36px" }}>
                  <div className="px-2 py-1" style={{ borderRight: `1px solid ${CELL}` }}>Fecha</div>
                  <div className="px-2 py-1" style={{ borderRight: `1px solid ${CELL}` }}>Estructura</div>
                  <div className="px-2 py-1" style={{ borderRight: `1px solid ${CELL}` }}>Recursos</div>
                  <div />
                </div>
                {(d.estructura || []).map((row, i) => (
                  <div key={i} className="grid" style={{ gridTemplateColumns: "110px 1fr 150px 36px", borderTop: `1px solid ${CELL}` }} data-testid={`pf-estr-${i}`}>
                    <div style={{ borderRight: `1px solid ${CELL}` }}>
                      <input value={row.fecha} onChange={(e) => setEstr(i, "fecha", e.target.value)} data-testid={`pf-estr-fecha-${i}`} className="w-full px-2 py-1.5 text-sm outline-none bg-transparent" />
                    </div>
                    <div style={{ borderRight: `1px solid ${CELL}` }}>
                      <AutoTextarea value={row.estructura} onChange={(e) => setEstr(i, "estructura", e.target.value)} testid={`pf-estr-txt-${i}`} minRows={2} />
                    </div>
                    <div style={{ borderRight: `1px solid ${CELL}` }}>
                      <AutoTextarea value={row.recursos} onChange={(e) => setEstr(i, "recursos", e.target.value)} testid={`pf-estr-rec-${i}`} minRows={2} />
                    </div>
                    <div className="flex items-center justify-center">
                      {(d.estructura || []).length > 1 && <button type="button" onClick={() => delEstr(i)} data-testid={`pf-estr-del-${i}`} className="text-nardo-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>}
                    </div>
                  </div>
                ))}
                <div className="p-2" style={{ borderTop: `1px solid ${CELL}` }}>
                  <button type="button" onClick={addEstr} data-testid="pf-estr-add" className="text-[11px] font-semibold inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-white" style={{ background: NAVY_BAND }}><Plus className="w-3.5 h-3.5" /> Agregar fila</button>
                </div>
              </div>

              {/* Orientaciones didácticas */}
              <div style={{ borderBottom: `1px solid ${CELL}` }}>
                <Band bg={GOLD} color="#3a2a00">Orientaciones didácticas</Band>
                {ta("orientaciones", "", 3)}
              </div>

              {/* Libros de texto */}
              <div style={{ borderBottom: `1px solid ${CELL}` }}>
                <Band>Libros de texto</Band>
                <div className="grid sm:grid-cols-4">
                  {LIBROS_PRE.map((b, i) => (
                    <div key={b.key} style={{ borderRight: i < 3 ? `1px solid ${CELL}` : "none" }}>
                      <div className="text-center text-[10px] font-bold px-1 py-1" style={{ background: GOLD, color: "#3a2a00" }}>{b.label}</div>
                      {ta(b.key, "", 2)}
                    </div>
                  ))}
                </div>
              </div>

              {/* Alumnos que requieren apoyo */}
              <div style={{ borderBottom: `1px solid ${CELL}` }}>
                <Band>Alumnos que requieren apoyo</Band>
                <div className="grid sm:grid-cols-2">
                  <div style={{ borderRight: `1px solid ${CELL}` }}>
                    <div className="text-center text-[10px] font-bold px-1 py-1" style={{ background: GOLD, color: "#3a2a00" }}>Listado de alumnos</div>
                    {ta("listado_alumnos", "", 3)}
                  </div>
                  <div>
                    <div className="text-center text-[10px] font-bold px-1 py-1" style={{ background: GOLD, color: "#3a2a00" }}>Estrategias pedagógicas</div>
                    {ta("estrategias_apoyo", "", 3)}
                  </div>
                </div>
              </div>

              {/* Programa de mejora continua */}
              <div style={{ borderBottom: `1px solid ${CELL}` }}>
                <Band>Programa de mejora continua</Band>
                {ta("mejora_continua", "Escribe aquí…", 2)}
                {MEJORA_PRE.map((p) => (
                  <div key={p.key} style={{ borderTop: `1px solid ${CELL}` }}>
                    <div className="text-center text-[10px] font-bold px-2 py-1" style={{ background: GOLD, color: "#3a2a00" }}>{p.label}</div>
                    {tam(p.key, "texto")}
                    <div className="grid text-[10px] font-bold" style={{ gridTemplateColumns: "1fr 1fr 1fr", borderTop: `1px solid ${CELL}` }}>
                      <div className="px-2 py-0.5 text-center" style={{ background: GREEN, color: "#fff", borderRight: `1px solid ${CELL}` }}>Estrategia</div>
                      <div className="px-2 py-0.5 text-center" style={{ background: GREEN, color: "#fff", borderRight: `1px solid ${CELL}` }}>Actividad</div>
                      <div className="px-2 py-0.5 text-center" style={{ background: GREEN, color: "#fff" }}>Producto de evidencia</div>
                    </div>
                    <div className="grid" style={{ gridTemplateColumns: "1fr 1fr 1fr" }}>
                      <div style={{ borderRight: `1px solid ${CELL}` }}>{tam(p.key, "estrategia")}</div>
                      <div style={{ borderRight: `1px solid ${CELL}` }}>{tam(p.key, "actividad")}</div>
                      <div>{tam(p.key, "producto")}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Evaluación */}
              <div style={{ borderBottom: `1px solid ${CELL}` }}>
                <Band bg={GOLD} color="#3a2a00">Evaluación</Band>
                {ta("evaluacion", "", 2)}
              </div>

              {/* Anexos */}
              <div>
                <Band bg={GOLD} color="#3a2a00">Anexos</Band>
                {ta("anexos", "", 2)}
              </div>
            </div>
          </div>
        ) : (
          /* ---------- FORMATO PRIMARIA Y SECUNDARIA (Plan de trabajo NEM tipo tabla) ---------- */
          <div className="p-4 sm:p-5">
            <input value={titulo} onChange={(e) => setTitulo(e.target.value)} placeholder="Nombre de la planeación (para tu lista)" data-testid="pf-titulo" className="w-full border border-nardo-200 rounded-lg px-3 py-2.5 text-sm text-navy outline-none focus:border-navy font-semibold mb-3" />

            <div className="mb-1 flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-nardo-500"><Palette className="w-3 h-3" /> Formato (color) — 10 opciones</div>
            <div className="flex flex-wrap gap-1.5 mb-2" data-testid="pf-formatos">
              {PLAN_THEMES.map((t) => (
                <button key={t.n} type="button" onClick={() => pickFormato(t)} title={`Formato ${t.n} · ${t.name}`} data-testid={`pf-formato-${t.n}`}
                  className={`w-8 h-8 rounded-lg border-2 flex items-center justify-center text-[10px] font-bold text-white ${formato === t.n ? "ring-2 ring-offset-1" : ""}`}
                  style={{ background: t.color, borderColor: formato === t.n ? t.color : "transparent" }}>{t.n}</button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-1.5 mb-4">
              <span className="text-[10px] text-nardo-400">Color personalizado:</span>
              {PALETTE.map((c) => <button key={c} type="button" onClick={() => setColor(c)} data-testid={`pf-color-${c}`} style={{ background: c }} className={`w-5 h-5 rounded border ${color === c ? "ring-2 ring-offset-1 ring-navy" : "border-nardo-200"}`} />)}
              <input type="color" value={color} onChange={(e) => setColor(e.target.value)} data-testid="pf-color-custom" className="w-7 h-6 rounded border border-nardo-200 cursor-pointer" />
            </div>

            <div className="border rounded-lg overflow-hidden text-navy" style={{ borderColor: CELL }} data-testid="pf-prim">
              <div className="text-center font-bold text-sm py-2" style={{ color: "#0f2747" }}>PLAN DE TRABAJO · PRIMARIA / SECUNDARIA</div>

              {/* Encabezado */}
              <div className="grid grid-cols-2 sm:grid-cols-5" style={{ borderTop: `1px solid ${CELL}` }}>
                {[["docente", "Docente"], ["grado", "Grado"], ["grupo", "Grupo"], ["no_ninas", "N° Niñas"], ["no_ninos", "N° Niños"]].map(([k, l], i) => (
                  <div key={k} className="p-1.5" style={{ borderRight: i < 4 ? `1px solid ${CELL}` : "none" }}>
                    <label className="block text-[9px] uppercase tracking-wide text-nardo-500 font-semibold">{l}</label>
                    <input value={d[k] || ""} onChange={set(k)} data-testid={`pf-${k}`} placeholder="—" className="w-full text-sm outline-none bg-transparent text-navy border-b border-dashed border-nardo-300 focus:border-navy placeholder:text-nardo-300" />
                  </div>
                ))}
              </div>

              {/* Estilos de aprendizaje */}
              <div className="grid grid-cols-2 sm:grid-cols-4" style={{ borderTop: `1px solid ${CELL}` }}>
                {NIVEL_ESTILOS.map(([k, l], i) => (
                  <div key={k} className="p-1.5" style={{ borderRight: i < 3 ? `1px solid ${CELL}` : "none" }}>
                    <label className="block text-[9px] uppercase tracking-wide text-nardo-500 font-semibold">{l}</label>
                    <input value={d[k] || ""} onChange={set(k)} data-testid={`pf-${k}`} placeholder="—" className="w-full text-sm outline-none bg-transparent text-navy border-b border-dashed border-nardo-300 focus:border-navy placeholder:text-nardo-300" />
                  </div>
                ))}
              </div>

              {/* Proyecto / Escenario */}
              <div className="grid sm:grid-cols-2" style={{ borderTop: `1px solid ${CELL}` }}>
                <div className="flex" style={{ borderRight: `1px solid ${CELL}` }}>
                  <div className="w-28 shrink-0 px-2 py-1.5 font-bold text-[11px]" style={{ background: STEEL, color: "#0f2747" }}>PROYECTO</div>
                  <input value={titulo} onChange={(e) => setTitulo(e.target.value)} data-testid="pf-proyecto-cell" placeholder="Escribe aquí" className="flex-1 px-2 py-1.5 text-sm outline-none bg-transparent placeholder:text-nardo-300 placeholder:text-[11px]" />
                </div>
                <div className="flex">
                  <div className="w-28 shrink-0 px-2 py-1.5 font-bold text-[11px]" style={{ background: STEEL, color: "#0f2747" }}>ESCENARIO</div>
                  <input value={d.escenario || ""} onChange={set("escenario")} data-testid="pf-escenario" placeholder="Escribe aquí" className="flex-1 px-2 py-1.5 text-sm outline-none bg-transparent placeholder:text-nardo-300 placeholder:text-[11px]" />
                </div>
              </div>

              {/* Temporalidad */}
              <div className="flex" style={{ borderTop: `1px solid ${CELL}` }}>
                <div className="w-28 shrink-0 px-2 py-1.5 font-bold text-[11px]" style={{ background: STEEL, color: "#0f2747" }}>TEMPORALIDAD</div>
                <div className="flex-1">{ta("temporalidad", "", 1)}</div>
              </div>

              {/* Ejes articuladores */}
              <div style={{ borderTop: `1px solid ${CELL}` }}>
                <Band bg={STEEL} color="#0f2747">Ejes articuladores</Band>
                <div className="grid grid-cols-4 sm:grid-cols-7">
                  {EJES_PRIM.map((e, i) => {
                    const on = (d.ejes || []).includes(e.key);
                    return (
                      <button type="button" key={e.key} onClick={() => toggle("ejes", e.key)} data-testid={`pf-eje-${e.key}`}
                        className="flex flex-col items-center gap-1 p-1.5 transition-colors" style={{ borderRight: `1px solid ${CELL}`, borderTop: `1px solid ${CELL}`, background: on ? "#e6eff7" : "#fff" }}>
                        <img src={ICON(e.icon)} alt="" className={`w-9 h-9 object-contain rounded ${on ? "" : "opacity-60"}`} />
                        <span className="text-[8px] leading-tight text-center font-semibold" style={{ color: on ? "#0f2747" : "#64748b" }}>{e.key}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Campo formativo / Contenido / Proceso de desarrollo */}
              <div style={{ borderTop: `1px solid ${CELL}` }}>
                <div className="grid" style={{ gridTemplateColumns: "150px 1fr 1.4fr" }}>
                  <div style={{ borderRight: `1px solid ${CELL}` }}><Band bg={STEEL} color="#0f2747">Campo formativo</Band></div>
                  <div style={{ borderRight: `1px solid ${CELL}` }}><Band bg={STEEL} color="#0f2747">Contenido</Band></div>
                  <div><Band bg={STEEL} color="#0f2747">Proceso de desarrollo</Band></div>
                </div>
                {CAMPOS_PRIM.map((c) => (
                  <div key={c.key} className="grid" style={{ gridTemplateColumns: "150px 1fr 1.4fr", borderTop: `1px solid ${CELL}` }}>
                    <div className="flex flex-col items-center justify-center gap-1 p-1.5">
                      <img src={ICON(c.icon)} alt="" className="w-10 h-10 object-contain rounded-lg" />
                      <span className="text-[9px] leading-tight text-center font-semibold text-navy">{c.label}</span>
                    </div>
                    <div style={{ borderLeft: `1px solid ${CELL}` }}>{ta(`cont_${c.key}`, "", 3)}</div>
                    <div style={{ borderLeft: `1px solid ${CELL}` }}>{ta(`proc_${c.key}`, "", 3)}</div>
                  </div>
                ))}
              </div>

              {/* Metodología de aprendizaje */}
              <div style={{ borderTop: `1px solid ${CELL}` }}>
                <Band bg={STEEL} color="#0f2747">Metodología de aprendizaje</Band>
                <div className="grid sm:grid-cols-4">
                  {METODOLOGIAS_PRIM.map(([k, l], i) => (
                    <div key={k} style={{ borderRight: i < 3 ? `1px solid ${CELL}` : "none" }}>
                      <div className="text-center text-[10px] font-bold px-1 py-1 bg-slate-100 text-navy">{l}</div>
                      {ta(k, "", 2)}
                    </div>
                  ))}
                </div>
              </div>

              {/* Perfil de egreso */}
              <div style={{ borderTop: `1px solid ${CELL}` }}>
                <Band bg={STEEL} color="#0f2747">Perfil de egreso</Band>
                {ta("perfil_egreso", "", 3)}
              </div>

              {/* Problemática / Cuestionamientos */}
              <div className="grid sm:grid-cols-2" style={{ borderTop: `1px solid ${CELL}` }}>
                <div style={{ borderRight: `1px solid ${CELL}` }}>
                  <Band bg={STEEL} color="#0f2747">Problemática · Contexto socioeducativo</Band>
                  <div className="flex" style={{ borderTop: `1px solid ${CELL}` }}>
                    <div className="w-24 shrink-0 px-2 py-1.5 font-bold text-[10px] bg-slate-100">General</div>
                    <div className="flex-1">{ta("problematica_general", "", 2)}</div>
                  </div>
                  <div className="flex" style={{ borderTop: `1px solid ${CELL}` }}>
                    <div className="w-24 shrink-0 px-2 py-1.5 font-bold text-[10px] bg-slate-100">Contexto</div>
                    <div className="flex-1">{ta("contexto_socio", "", 2)}</div>
                  </div>
                  <div className="p-1.5" style={{ borderTop: `1px solid ${CELL}` }}>
                    <div className="text-[10px] font-bold text-navy mb-1">Habilidades básicas</div>
                    <div className="flex items-center gap-2 mb-1"><span className="text-[10px] w-24 text-nardo-500">Lectoescritura:</span>
                      <input value={d.hab_lectoescritura || ""} onChange={set("hab_lectoescritura")} data-testid="pf-hab_lectoescritura" className="flex-1 text-sm outline-none bg-transparent border-b border-dashed border-nardo-300 focus:border-navy" /></div>
                    <div className="flex items-center gap-2"><span className="text-[10px] w-24 text-nardo-500">Cálculo mental:</span>
                      <input value={d.hab_calculo || ""} onChange={set("hab_calculo")} data-testid="pf-hab_calculo" className="flex-1 text-sm outline-none bg-transparent border-b border-dashed border-nardo-300 focus:border-navy" /></div>
                  </div>
                </div>
                <div>
                  <Band bg={STEEL} color="#0f2747">Cuestionamientos de conocimientos previos y/o detonadoras</Band>
                  {ta("cuestionamientos", "Cuestionamientos para saber lo que saben los alumnos", 5)}
                </div>
              </div>

              {/* Actividad rompe hielo */}
              <div style={{ borderTop: `1px solid ${CELL}` }}>
                <Band bg={STEEL} color="#0f2747">Actividad rompe hielo · se trabajará una actividad todos los días</Band>
                {ta("rompehielo", "", 2)}
              </div>

              {/* Horario */}
              <div style={{ borderTop: `1px solid ${CELL}` }}>
                <Band bg={GOLD} color="#3a2a00">Horario</Band>
                <div className="grid grid-cols-5">
                  {DIAS_PRIM.map(([k, l], i) => (
                    <div key={k} style={{ borderRight: i < 4 ? `1px solid ${CELL}` : "none" }}>
                      <div className="text-center text-[10px] font-bold px-1 py-1 bg-slate-100 text-navy">{l}</div>
                      {ta(k, "", 3)}
                    </div>
                  ))}
                </div>
              </div>

              {/* Estructura del proyecto */}
              <div style={{ borderTop: `1px solid ${CELL}` }}>
                <div className="grid text-[10px] font-bold" style={{ gridTemplateColumns: "110px 1fr 150px 36px", background: GOLD, color: "#3a2a00" }}>
                  <div className="px-2 py-1.5 text-center" style={{ borderRight: `1px solid ${CELL}` }}>Fecha</div>
                  <div className="px-2 py-1.5 text-center" style={{ borderRight: `1px solid ${CELL}` }}>Estructura del proyecto</div>
                  <div className="px-2 py-1.5 text-center" style={{ borderRight: `1px solid ${CELL}` }}>Recursos e implicaciones</div>
                  <div />
                </div>
                {(d.estructura || []).map((row, i) => (
                  <div key={i} className="grid" style={{ gridTemplateColumns: "110px 1fr 150px 36px", borderTop: `1px solid ${CELL}` }} data-testid={`pf-estr-${i}`}>
                    <div style={{ borderRight: `1px solid ${CELL}` }}><input value={row.fecha} onChange={(e) => setEstr(i, "fecha", e.target.value)} data-testid={`pf-estr-fecha-${i}`} className="w-full px-2 py-1.5 text-sm outline-none bg-transparent" /></div>
                    <div style={{ borderRight: `1px solid ${CELL}` }}><AutoTextarea value={row.estructura} onChange={(e) => setEstr(i, "estructura", e.target.value)} testid={`pf-estr-txt-${i}`} minRows={3} /></div>
                    <div style={{ borderRight: `1px solid ${CELL}` }}><AutoTextarea value={row.recursos} onChange={(e) => setEstr(i, "recursos", e.target.value)} testid={`pf-estr-rec-${i}`} minRows={3} /></div>
                    <div className="flex items-center justify-center">{(d.estructura || []).length > 1 && <button type="button" onClick={() => delEstr(i)} data-testid={`pf-estr-del-${i}`} className="text-nardo-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>}</div>
                  </div>
                ))}
                <div className="p-2" style={{ borderTop: `1px solid ${CELL}` }}>
                  <button type="button" onClick={addEstr} data-testid="pf-estr-add" className="text-[11px] font-semibold inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-white" style={{ background: color }}><Plus className="w-3.5 h-3.5" /> Agregar fila</button>
                </div>
              </div>

              {/* Aspectos a evaluar */}
              <div style={{ borderTop: `1px solid ${CELL}` }}>
                <Band bg={STEEL} color="#0f2747">Aspectos a evaluar</Band>
                {APOYO_PRIM.map(([k, l]) => (
                  <div key={k} style={{ borderTop: `1px solid ${CELL}` }}>
                    <div className="grid sm:grid-cols-2 text-[10px] font-bold bg-slate-100 text-navy">
                      <div className="px-2 py-1" style={{ borderRight: `1px solid ${CELL}` }}>{l}</div>
                      <div className="px-2 py-1">Estrategias pedagógicas</div>
                    </div>
                    <div className="grid sm:grid-cols-2">
                      <div style={{ borderRight: `1px solid ${CELL}` }}>{ta(`${k}_lista`, "", 2)}</div>
                      <div>{ta(`${k}_estr`, "", 2)}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* PEMC */}
              <div style={{ borderTop: `1px solid ${CELL}` }}>
                <Band bg={STEEL} color="#0f2747">PEMC</Band>
                {ta("pemc", "Escribe aquí el PEMC…", 2)}
                <div className="text-center text-[10px] font-bold px-1 py-1 bg-slate-100 text-navy" style={{ borderTop: `1px solid ${CELL}` }}>Actividades</div>
                {[["comprension_lectora", "Comprensión lectora"], ["textos_escritos", "Textos escritos"], ["calculo_mental", "Cálculo mental"]].map(([k, l]) => (
                  <div key={k} className="flex" style={{ borderTop: `1px solid ${CELL}` }}>
                    <div className="w-40 shrink-0 px-2 py-1.5 font-bold text-[10px] bg-slate-50 text-navy" style={{ borderRight: `1px solid ${CELL}` }}>{l}</div>
                    <div className="flex-1">{ta(k, "", 2)}</div>
                  </div>
                ))}
              </div>

              {/* Medidas preventivas para salvaguardar la integridad */}
              <div style={{ borderTop: `1px solid ${CELL}` }}>
                <Band bg={STEEL} color="#0f2747">Medidas preventivas para salvaguardar la integridad de estudiantes de educación básica</Band>
                {ta("medidas_integridad", "Escribe aquí las medidas preventivas…", 3)}
              </div>

              {/* Evaluación */}
              <div style={{ borderTop: `1px solid ${CELL}` }}>
                <Band bg={STEEL} color="#0f2747">Evaluación</Band>
                <div className="grid sm:grid-cols-2">
                  <div style={{ borderRight: `1px solid ${CELL}` }}>
                    <div className="text-center text-[10px] font-bold px-1 py-1 bg-slate-100 text-navy">Instrumento</div>
                    {ta("eval_instrumento", "Ej. Lista de cotejo, escala valorativa, guía de observación", 3)}
                  </div>
                  <div>
                    <div className="text-center text-[10px] font-bold px-1 py-1 bg-slate-100 text-navy">Aprendizaje</div>
                    {SABERES_PRIM.map(([k, l]) => (
                      <div key={k} className="flex" style={{ borderTop: `1px solid ${CELL}` }}>
                        <div className="w-40 shrink-0 px-2 py-1.5 font-bold text-[10px] bg-slate-50 text-navy" style={{ borderRight: `1px solid ${CELL}` }}>{l}</div>
                        <div className="flex-1">{ta(k, "", 1)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Ajuste razonable / Codiseño / Observaciones */}
              <div style={{ borderTop: `1px solid ${CELL}` }}>
                <Band bg={STEEL} color="#0f2747">Ajuste razonable del proyecto</Band>
                {ta("ajuste_razonable", "", 2)}
              </div>
              <div style={{ borderTop: `1px solid ${CELL}` }}>
                <Band bg={STEEL} color="#0f2747">Codiseño</Band>
                {ta("codiseno", "", 2)}
              </div>
              <div style={{ borderTop: `1px solid ${CELL}` }}>
                <Band bg={STEEL} color="#0f2747">Observaciones</Band>
                {ta("observaciones_fin", "", 2)}
              </div>
            </div>
          </div>
        )}

        <div className="px-4 sm:px-5 pb-1">
          <FirmasEditor d={d} set={set} base="pf-firmas" color={color} />
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-nardo-200">
          <span className="mr-auto self-center text-[11px] text-nardo-400" data-testid="pf-autosave-hint">Autoguardado activo</span>
          <button onClick={onCancel} className="text-sm px-4 py-2 rounded-lg text-nardo-600 hover:bg-nardo-100">Cerrar</button>
          <button onClick={() => save(false)} disabled={saving} data-testid="pf-save" className="text-sm px-4 py-2 rounded-lg text-white font-semibold inline-flex items-center gap-1.5 disabled:opacity-50" style={{ background: color }}>{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar cambios y contenido</button>
        </div>
      </div>
    </div>
  );
}
